import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

export class QuickAddComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    // Handle direct add-to-cart form submissions
    this.addEventListener('submit', this.#handleDirectAddToCart);
    
    // Ensure "Choose Options" links are not intercepted
    this.addEventListener('click', this.#handleChooseOptionsClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('submit', this.#handleDirectAddToCart);
    this.removeEventListener('click', this.#handleChooseOptionsClick, true);
  }

  /**
   * Ensures "Choose Options" links navigate directly without modal
   * @param {Event} event - The click event
   */
  #handleChooseOptionsClick = (event) => {
    const target = event.target;
    const chooseOptionsLink = target.closest('.quick-add__button--choose-options');
    
    if (chooseOptionsLink) {
      // Let the default link behavior happen (redirect to product page)
      // Stop any parent handlers from intercepting
      event.stopPropagation();
    }
  };

  /**
   * Handles direct add-to-cart without modal
   * @param {Event} event - The submit event
   */
  #handleDirectAddToCart = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.getAttribute('data-type') !== 'add-to-cart-form') return;

    event.preventDefault();
    event.stopPropagation();

    const button = form.querySelector('.quick-add__button--add');
    if (!button) return;

    const addToCartText = button.getAttribute('data-add-to-cart-text');
    const addedText = button.getAttribute('data-added-text');
    const textElement = button.querySelector('.add-to-cart-text__content');
    const formData = new FormData(form);

    // Change button text to "Adding..."
    if (textElement) {
      textElement.textContent = 'Adding...';
    }
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();

      // Success - show "Added" text
      if (textElement) {
        textElement.textContent = addedText;
      }

      // Dispatch cart update event
      window.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      
      // Also dispatch theme cart update event if available
      if (typeof CartUpdateEvent !== 'undefined') {
        this.dispatchEvent(new CartUpdateEvent({ data: { didError: false } }));
      }

      // Revert back after 2 seconds
      setTimeout(() => {
        if (textElement) {
          textElement.textContent = addToCartText;
        }
        button.disabled = false;
      }, 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (textElement) {
        textElement.textContent = 'Error';
      }
      setTimeout(() => {
        if (textElement) {
          textElement.textContent = addToCartText;
        }
        button.disabled = false;
      }, 2000);
    }
  };
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

// Keep the QuickAddDialog class if it's used elsewhere in your theme
// Otherwise you can remove it completely
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { getIOSVersion } from '@theme/utilities';

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}